import { dayOutConditionsHref, dayOutFromRecommendation, withDayOut } from "@/lib/companion/day-out";
import type {
  CompanionHomeData,
  CompanionHomeModel,
  CompanionSavedPlace,
} from "@/lib/companion/home-model";
import type { CompanionDecisionAction } from "@/lib/companion/decision";
import type {
  CompanionRankedRecommendation,
  CompanionRecommendationCandidate,
  CompanionRecommendationEmptyReason,
} from "@/lib/companion/recommendation-types";
import {
  COMPANION_GO_LATER_SCORE,
  COMPANION_GO_NOW_SCORE,
  ELSEWHERE_SCORE_GAP,
} from "@/lib/companion/decision";
import { normaliseLocalSport } from "@/lib/companion/local-comparison-links";
import { scoreOutOf100Label } from "@/lib/conditions/score-display";
import { sportLabel } from "@/lib/conditions/sport-meta";
import {
  companionSafetyLine,
  decisionActionLabel,
  plannerHrefForPlace,
  type CompanionNearbyBetterLine,
  type CompanionZoneAState,
} from "./companion-home-state";

/**
 * The home pick weighs drive time (and free time when set), so it is labelled as such to keep
 * it distinct from the local comparison's "Best conditions in {Area}" (spec 10.7).
 */
export const COMPANION_PICK_LABEL = "Best for you (includes drive time)";

export type CompanionRecommendationAction = {
  kind: "call" | "plan" | "track" | "conditions" | "setup";
  label: string;
  href: string;
};

export type CompanionRecommendationModel = {
  state: "setup" | "ready" | "unavailable";
  emptyReason?: CompanionRecommendationEmptyReason | null;
  label: typeof COMPANION_PICK_LABEL;
  decisionAction: CompanionDecisionAction;
  decisionLabel: string;
  sportKey: string | null;
  sportLabel: string | null;
  placeName: string | null;
  placeRegion: string | null;
  score: number | null;
  verdict: string;
  reason: string;
  windowLine: string | null;
  fitLine: string | null;
  nextUsefulLine: string | null;
  confidenceLabel: string;
  freshnessLabel: string;
  safetyLine: string;
  confirmedLabel: string | null;
  callTakeover: {
    title: string;
    windowCopy: string | null;
    href?: string;
  } | null;
  alternatives: {
    kind: "cross-base" | "nearby";
    label: string;
    href: string;
  }[];
  downDay: CompanionHomeData["downDay"];
  primaryAction: CompanionRecommendationAction;
  secondaryAction: CompanionRecommendationAction | null;
};

type BuildCompanionRecommendationInput = {
  data: CompanionHomeData;
  model: CompanionHomeModel;
  zone: CompanionZoneAState;
  now?: Date;
  crossSport?: {
    bestForYou: CompanionRankedRecommendation | null;
    emptyReason?: CompanionRecommendationEmptyReason | null;
  };
};

export function buildCompanionRecommendationModel(
  input: BuildCompanionRecommendationInput,
): CompanionRecommendationModel {
  if (input.crossSport) {
    return buildCrossSportRecommendationModel(input, input.crossSport.bestForYou);
  }

  const { model, zone } = input;
  const recommendationPlace = model.decisionPlace ?? model.visiblePlace;
  const recommendationSportKey = companionRecommendationSportKey(recommendationPlace);
  const primaryAction = primaryRecommendationAction(model, recommendationPlace);

  return {
    state: recommendationPlace ? "ready" : "setup",
    label: COMPANION_PICK_LABEL,
    decisionAction: model.decision.action,
    decisionLabel: decisionActionLabel(model.decision.action),
    sportKey: recommendationSportKey,
    sportLabel: recommendationSportKey
      ? sportLabel(recommendationSportKey)
      : fallbackSportLabel(recommendationPlace),
    placeName: recommendationPlace?.name ?? null,
    placeRegion: recommendationPlace?.region ?? null,
    score: recommendationPlace?.score ?? null,
    verdict:
      !recommendationPlace && zone.verdict.actionTone === "not-today"
        ? "Your first verdict is one saved place away."
        : zone.verdict.verdict,
    reason: zone.verdict.reason,
    windowLine: zone.verdict.windowLine,
    fitLine: hasUsableRecommendationWindow(recommendationPlace)
      ? zone.verdict.fitLine
      : null,
    nextUsefulLine: zone.verdict.nextUsefulLine,
    confidenceLabel: zone.verdict.confidenceLabel,
    freshnessLabel: zone.verdict.freshnessLabel,
    safetyLine: companionSafetyLine(
      recommendationPlace,
      null,
      zone.verdict.freshnessLabel,
    ),
    confirmedLabel: zone.verdict.confirmedLabel,
    callTakeover: zone.callTakeover
      ? {
          title: zone.callTakeover.title,
          windowCopy: zone.callTakeover.windowCopy,
          href: zone.callTakeover.href,
        }
      : null,
    alternatives: continuationAlternatives(zone),
    downDay: zone.downDay,
    primaryAction,
    secondaryAction: secondaryRecommendationAction(
      model,
      recommendationPlace,
      primaryAction,
    ),
  };
}

export function buildCrossSportRecommendationModel(
  input: Pick<BuildCompanionRecommendationInput, "zone" | "now" | "crossSport">,
  ranked: CompanionRankedRecommendation | null,
): CompanionRecommendationModel {
  const { zone } = input;
  if (!ranked) {
    const emptyReason = input.crossSport?.emptyReason ?? "no-sports";
    const empty = emptyRecommendationCopy(emptyReason);
    return {
      state: emptyReason === "no-sports" || emptyReason === "no-base" ? "setup" : "unavailable",
      emptyReason,
      label: COMPANION_PICK_LABEL,
      decisionAction: "do-not-bother",
      decisionLabel: empty.label,
      sportKey: null,
      sportLabel: null,
      placeName: null,
      placeRegion: null,
      score: null,
      verdict: empty.verdict,
      reason: empty.reason,
      windowLine: null,
      fitLine: null,
      nextUsefulLine: null,
      confidenceLabel: "No recommendation yet",
      freshnessLabel: "No fresh score",
      safetyLine: "Check conditions and safety before deciding.",
      confirmedLabel: null,
      callTakeover: null,
      alternatives: continuationAlternatives(zone),
      downDay: zone.downDay,
      primaryAction: {
        kind: emptyReason === "no-sports" || emptyReason === "no-base" ? "setup" : "conditions",
        label: empty.action,
        href: empty.href,
      },
      secondaryAction: null,
    };
  }

  const selectedCandidate = ranked.candidate;
  const dayOut = dayOutFromRecommendation(selectedCandidate);
  const candidate = { ...selectedCandidate,
    conditionsHref: dayOut ? dayOutConditionsHref(dayOut) : selectedCandidate.conditionsHref,
    plannerHref: selectedCandidate.plannerHref ? withDayOut(selectedCandidate.plannerHref, dayOut) : null,
  };
  const hasWindow = Boolean(candidate.remainingWindow || candidate.nextWindow);
  const decisionAction: CompanionDecisionAction =
    candidate.remainingWindow &&
      candidate.score >= COMPANION_GO_NOW_SCORE &&
      candidate.safetyState !== "flagged-red"
      ? "go-now"
      : hasWindow && candidate.score >= COMPANION_GO_LATER_SCORE
        ? "go-later"
        : "do-not-bother";
  const primaryAction = crossSportPrimaryAction(
    candidate,
    decisionAction,
  );
  return {
    state: "ready",
    label: COMPANION_PICK_LABEL,
    decisionAction,
    decisionLabel: decisionActionLabel(decisionAction),
    sportKey: candidate.sportKey,
    sportLabel: sportLabel(candidate.sportKey),
    placeName: candidate.placeName,
    placeRegion: candidate.region,
    score: candidate.score,
    verdict: candidate.verdict ?? `${sportLabel(candidate.sportKey)} looks strongest.`,
    reason: ranked.explanation.whySport,
    windowLine: candidate.remainingWindow ?? candidate.nextWindow,
    fitLine:
      candidate.fitsAvailability === true
        ? "Fits your free time."
        : null,
    nextUsefulLine: candidate.nextWindow,
    confidenceLabel: `${capitalise(candidate.confidence)} confidence`,
    freshnessLabel: recommendationFreshness(candidate.updatedAt, input.now ?? new Date()),
    safetyLine: crossSportSafetyLine(candidate),
    confirmedLabel: candidate.callSupported ? "Followed for the Call" : null,
    callTakeover: zone.callTakeover
      ? {
          title: zone.callTakeover.title,
          windowCopy: zone.callTakeover.windowCopy,
          href: zone.callTakeover.href,
        }
      : null,
    alternatives: continuationAlternatives(zone, (line) => nearbyLineFitsPick(line, selectedCandidate)),
    downDay: zone.downDay,
    primaryAction,
    secondaryAction:
      candidate.conditionsHref !== primaryAction.href
        ? {
            kind: "conditions",
            label: "Open conditions",
            href: candidate.conditionsHref,
          }
        : null,
  };
}

function crossSportPrimaryAction(
  candidate: CompanionRankedRecommendation["candidate"],
  decisionAction: CompanionDecisionAction,
): CompanionRecommendationAction {
  if (candidate.plannerHref) {
    return { kind: "plan", label: "Plan the session", href: candidate.plannerHref };
  }
  if (decisionAction === "go-now" && candidate.remainingWindow) {
    return { kind: "track", label: "Set up recording", href: withDayOut("/companion/track", dayOutFromRecommendation(candidate)) };
  }
  return {
    kind: "conditions",
    label: "Open conditions",
    href: candidate.conditionsHref,
  };
}

function crossSportSafetyLine(
  candidate: CompanionRankedRecommendation["candidate"],
): string {
  if (candidate.safetyNotes[0]) return candidate.safetyNotes[0];
  if (candidate.safetyState === "flagged-amber") {
    return "A sport-specific safety caution applies.";
  }
  if (candidate.safetyState === "no-data") {
    return "PeakWave has no safety data for this sport yet, so check conditions yourself before heading out.";
  }
  return "No sport-specific safety flags in the stored forecast.";
}

function capitalise(value: string): string {
  return `${value.slice(0, 1).toUpperCase()}${value.slice(1)}`;
}

function primaryRecommendationAction(
  model: CompanionHomeModel,
  recommendationPlace: CompanionSavedPlace | null,
): CompanionRecommendationAction {

  if (recommendationPlace?.safetyState === "flagged-red") {
    return {
      kind: "conditions",
      label: "Check safety",
      href: recommendationPlace.href,
    };
  }

  const plannerHref = recommendationPlace
    ? plannerHrefForPlace(recommendationPlace)
    : null;
  if (plannerHref) {
    return { kind: "plan", label: "Plan the session", href: plannerHref };
  }

  if (recommendationPlace && model.decision.action === "go-now") {
    return { kind: "track", label: "Track now", href: "/companion/track" };
  }

  if (recommendationPlace) {
    return {
      kind: "conditions",
      label: "Open conditions",
      href: recommendationPlace.href,
    };
  }

  return { kind: "setup", label: "Save a place", href: "/conditions" };
}

function secondaryRecommendationAction(
  model: CompanionHomeModel,
  recommendationPlace: CompanionSavedPlace | null,
  primaryAction: CompanionRecommendationAction,
): CompanionRecommendationAction | null {
  if (recommendationPlace && recommendationPlace.href !== primaryAction.href) {
    return {
      kind: "conditions",
      label: "Open conditions",
      href: recommendationPlace.href,
    };
  }

  if (
    recommendationPlace &&
    model.decision.action === "go-now" &&
    recommendationPlace.safetyState !== "flagged-red" &&
    primaryAction.href !== "/companion/track"
  ) {
    return { kind: "track", label: "Track now", href: "/companion/track" };
  }

  return null;
}

/**
 * The zone's nearby-better lines are built against the saved-place pick. When Today shows the
 * cross-sport pick instead, keep only lines in that pick's sport family which are not the pick
 * itself and still beat it by the nearby gap (spec 10.7).
 */
export function nearbyLineFitsPick(
  line: CompanionNearbyBetterLine,
  pick: Pick<CompanionRecommendationCandidate, "slug" | "sportKey" | "score" | "conditionsHref">,
): boolean {
  const pickFamily = normaliseLocalSport(pick.sportKey);
  const lineFamily = normaliseLocalSport(line.sportKey ?? line.sportLabel);
  if (pickFamily && lineFamily !== pickFamily) return false;
  if (line.slug && pick.slug && line.slug === pick.slug) return false;
  if (hrefPath(line.href) === hrefPath(pick.conditionsHref)) return false;
  return line.score - pick.score >= ELSEWHERE_SCORE_GAP - 1e-9;
}

function hrefPath(href: string): string {
  return href.split(/[?#]/, 1)[0] ?? href;
}

function continuationAlternatives(
  zone: CompanionZoneAState,
  keepNearby: (line: CompanionNearbyBetterLine) => boolean = () => true,
): CompanionRecommendationModel["alternatives"] {
  const alternatives: CompanionRecommendationModel["alternatives"] = [];

  if (zone.crossBaseLine) {
    alternatives.push({
      kind: "cross-base",
      label: `${zone.crossBaseLine.name} at ${zone.crossBaseLine.baseName} · ${scoreOutOf100Label(zone.crossBaseLine.score)}`,
      href: zone.crossBaseLine.href,
    });
  }

  const nearbyLines = zone.nearbyBetterLines?.length
    ? zone.nearbyBetterLines
    : zone.nearbyBetterLine ? [zone.nearbyBetterLine] : [];
  for (const line of nearbyLines) {
    if (!keepNearby(line)) continue;
    if (alternatives.some((alternative) => alternative.href === line.href)) continue;
    alternatives.push({
      kind: "nearby",
      label: `${line.name} · ${line.sportLabel} · ${scoreOutOf100Label(line.score)} · about ${line.driveMinutes} min`,
      href: line.href,
    });
  }

  return alternatives.slice(0, 2);
}

function companionRecommendationSportKey(
  place: CompanionSavedPlace | null,
): string | null {
  const contextualKey = place?.preferredSport?.trim() || place?.locationType?.trim();
  if (contextualKey) return contextualKey;
  if (place?.sport === "ski" || place?.sport === "surf") return place.sport;
  return null;
}

function fallbackSportLabel(place: CompanionSavedPlace | null): string | null {
  if (place?.sport === "festival") return "Festival";
  if (place?.sport === "mixed") return "Mixed conditions";
  return null;
}

function hasUsableRecommendationWindow(place: CompanionSavedPlace | null): boolean {
  return Boolean(
    currentBestWindow(place),
  );
}

function currentBestWindow(
  place: Pick<CompanionSavedPlace, "remainingBestWindow" | "bestWindow"> | null | undefined,
): string | undefined {
  if (!place) return undefined;
  if (place.remainingBestWindow !== undefined) {
    return place.remainingBestWindow?.trim() || undefined;
  }
  return place.bestWindow?.trim() || undefined;
}

function recommendationFreshness(updatedAt: string | null, now: Date): string {
  const age = updatedAt ? now.getTime() - new Date(updatedAt).getTime() : NaN;
  if (!Number.isFinite(age) || age < 0) return "No fresh score";
  return age <= 6 * 60 * 60 * 1000
    ? "Updated in the last six hours"
    : "Stored update is over six hours old";
}

function emptyRecommendationCopy(reason: CompanionRecommendationEmptyReason) {
  const defaults = { label: "No suitable session yet", action: "Explore conditions", href: "/conditions" };
  switch (reason) {
    case "no-sports": return { ...defaults, label: "Set up your sports", verdict: "Tell PeakWave which sports you do.", reason: "Choose your sports to get a personal recommendation.", action: "Choose your sports", href: "/companion#companion-setup" };
    case "no-base": return { ...defaults, label: "Choose your base", verdict: "Where are you heading out from?", reason: "Save a base so PeakWave can find sessions within reach.", action: "Choose a base", href: "/companion#companion-setup" };
    case "missing-data": return { ...defaults, verdict: "Waiting for today's forecast.", reason: "Your sports are saved. We do not have today's score and session window for them nearby yet." };
    case "stale-data": return { ...defaults, verdict: "The forecast needs an update.", reason: "Your sports are saved. The available forecast is too old for a useful recommendation." };
    case "no-window": return { ...defaults, verdict: "No useful window left today.", reason: "Today's windows have ended, cannot be reached in time, or are not available yet. Check Plan for the next opportunity.", action: "Check Plan", href: "/companion/plan" };
    case "safety-unknown": return { ...defaults, verdict: "Safety information is missing.", reason: "Stored conditions do not include enough safety evidence to confirm a session. Review local advice and another option.", action: "Review conditions" };
    case "safety": return { ...defaults, verdict: "Safety flags rule out today's options.", reason: "Check the safety details and local advice before making plans.", action: "Check safety" };
    case "ability": return { ...defaults, verdict: "Review your experience for these sessions.", reason: "The available sessions need confirmed experience. Your current ability details do not establish a match." };
    case "availability": return { ...defaults, verdict: "Today's windows miss your free time.", reason: "Check Plan for another day, or update your availability if your plans have changed.", action: "Update free time", href: "/companion/plan/free-time" };
    case "travel": return { ...defaults, verdict: "Today's options are beyond your travel limit.", reason: "Check Plan for another day, or update how far you are happy to travel.", action: "Update travel limit", href: "/companion/settings?filter=availability" };
  }
}
